import _ from 'underscore';
import { useCallback, useContext, useMemo } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useSiteConfig, useAllPilots } from '@bingads-webui-campaign-react/smart-page-context';
import {
  COMPONENT_TYPE_MIGRATION_STATUS_KEY_MAPPING,
  getComponentById, SMART_PAGE_DEFAULT_VALUES,
  getWebsiteSourceDataByKey,
  getWebsiteSourceDataByKeys,
  truncateString,
  WEBSITE_SOURCE_DATA_KEY_MAPPING,
  isMigratedToWSD,
} from '@bingads-webui-campaign-react/smart-component-util';

import { proxyReadIntroductionComponentConfig } from './component-config-reader';
import { dataProperties, dataPropertiesWSDKeys, headlineMaxCount } from './data-config';

export const getSmartIntroductionDataFromAttributes = (siteConfig, componentId, i18n) => {
  const componentConfig = getComponentById(siteConfig, componentId);

  return proxyReadIntroductionComponentConfig(componentConfig, siteConfig, i18n);
};

export const getSmartIntroductionDataFromWSD = (siteConfig, i18n) => {
  const [
    businessName,
    businessCategories,
    businessDescription,
    introduction,
    logo,
  ] = getWebsiteSourceDataByKeys(siteConfig, [
    WEBSITE_SOURCE_DATA_KEY_MAPPING.BusinessName,
    WEBSITE_SOURCE_DATA_KEY_MAPPING.Categories,
    WEBSITE_SOURCE_DATA_KEY_MAPPING.Description,
    WEBSITE_SOURCE_DATA_KEY_MAPPING.Introduction,
    WEBSITE_SOURCE_DATA_KEY_MAPPING.Logo,
  ]);

  const {
    BusinessHeadline: headline,
    Description: description,
  } = introduction || {};

  const {
    ImageId: imageId,
    ImageUrl: imageUrl,
    ImageFileName: fileName,
  } = logo || {};

  const descriptionFromCategory = businessCategories && businessCategories.join(', ');

  return {
    headline,
    defaultHeadline: truncateString(businessName || i18n.getString(SMART_PAGE_DEFAULT_VALUES.BUSINESS_NAME), headlineMaxCount),
    description,
    defaultDescription: businessDescription || descriptionFromCategory || i18n.getString(SMART_PAGE_DEFAULT_VALUES.DESCRIPTION),
    logo: {
      imageId,
      imageUrl,
      fileName,
    },
  };
};

export const getSmartIntroductionData = (siteConfig, componentId, i18n, pilots) => {
  const { isSmartPageAttributesToWSDMigrationEnabled } = pilots;
  const shouldReadFromWSD = isSmartPageAttributesToWSDMigrationEnabled && isMigratedToWSD(siteConfig, COMPONENT_TYPE_MIGRATION_STATUS_KEY_MAPPING.Introduction);

  if (shouldReadFromWSD) {
    return getSmartIntroductionDataFromWSD(siteConfig, i18n);
  }

  return getSmartIntroductionDataFromAttributes(siteConfig, componentId, i18n);
};

export const getUpdateDataInAttributes = (siteConfig, componentId, property, value) => {
  const currentData = getComponentById(siteConfig, componentId);
  return { ...currentData, [property]: value };
};

export const getUpdateDataInWSD = (siteConfig, componentId, property, value) => {
  const currentDataInAttributes = getComponentById(siteConfig, componentId);
  const currentDataInWSD = getWebsiteSourceDataByKey(siteConfig, WEBSITE_SOURCE_DATA_KEY_MAPPING.Introduction);

  const data = _.defaults(currentDataInWSD, {
    [dataPropertiesWSDKeys.headline]: currentDataInAttributes[dataProperties.headline],
    [dataPropertiesWSDKeys.description]: currentDataInAttributes[dataProperties.description],
  });

  return {
    name: WEBSITE_SOURCE_DATA_KEY_MAPPING.Introduction,
    value: {
      ...data,
      [dataPropertiesWSDKeys[property]]: value,
    },
  };
};

export const dispatchUpdate = ({
  dispatch, siteConfig, componentId, property, value, pilots,
}) => {
  dispatch({
    type: 'UPDATE_COMPONENT_BY_ID',
    payload: getUpdateDataInAttributes(siteConfig, componentId, property, value),
  });

  /**
     * TODO: While the components are in the process of being migrate to WSD,
     * we need to have this logic in place to know if we should get/update the component data from attributes or from WSD.
     * After migrating all the components and the layout, this can be removed.
     * https://msasg.visualstudio.com/Bing_Ads/_workitems/edit/4729061
     */
  if (pilots.isSmartPageAttributesToWSDMigrationEnabled && !isMigratedToWSD(siteConfig, COMPONENT_TYPE_MIGRATION_STATUS_KEY_MAPPING.Introduction)) {
    dispatch({
      type: 'UPDATE_WSD_MIGRATION_STATUS',
      payload: {
        [COMPONENT_TYPE_MIGRATION_STATUS_KEY_MAPPING.Introduction]: true,
      },
    });
  }

  if (pilots.isSmartPageAttributesToWSDMigrationEnabled || isMigratedToWSD(siteConfig, COMPONENT_TYPE_MIGRATION_STATUS_KEY_MAPPING.Introduction)) {
    dispatch({
      type: 'UPDATE_WEBSITESOURCEDATA_BY_NAME',
      payload: getUpdateDataInWSD(siteConfig, componentId, property, value),
    });
  }
};

export const useSmartIntroductionData = (componentId) => {
  const i18n = useContext(I18nContext);
  const pilots = useAllPilots();

  const { siteConfig, dispatchSiteConfig: dispatch } = useSiteConfig();

  const data = getSmartIntroductionData(siteConfig, componentId, i18n, pilots);

  const update = useCallback(
    (property, value) =>
      dispatchUpdate({
        dispatch, siteConfig, componentId, property, value, pilots,
      })
    , [siteConfig, dispatch, componentId, pilots]
  );

  const debouncedUpdate = useMemo(() => _.debounce(update, 10), [update]);

  return { ...data, update: debouncedUpdate };
};
